import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IHost } from '../interfaces/host.interface';

/**
 * Servicio encargado de gestionar la lógica relacionada con los hosts o servidores.
 *
 * Este servicio está disponible globalmente en toda la aplicación, gracias a `providedIn: 'root'`,
 * lo que significa que solo habrá una instancia de este servicio en toda la app.
 *
 * Suele ser utilizado para interactuar con información de hosts, como obtener su estado,
 * configuraciones, realizar operaciones etc.
 */
@Injectable({
  providedIn: 'root',
})
export class HostService {
  /**
   * URL del endpoint para acceder a los hosts.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para obtener la lista de hosts disponibles en la aplicación.
   */
  private url: string = '/hosts';

  /**
   * Constructor de la clase HostService.
   *
   * Inyecta el servicio `HttpClient`, que se utiliza para realizar solicitudes HTTP
   * al servidor de la API y gestionar las operaciones relacionadas con los hosts.
   *
   * @param http - Servicio de Angular para realizar solicitudes HTTP.
   */
  constructor(private http: HttpClient) {}

  /**
   * Obtiene una lista de hosts con filtros opcionales.
   *
   * @param disabled - Filtra los hosts según su estado (activo/inactivo). `null` para ignorar el filtro.
   * @param offset - Define el desplazamiento para la paginación. `null` si no se usa paginación.
   * @param limit - Establece el número máximo de hosts a devolver. `null` para no limitar los resultados.
   *
   * @returns Una promesa que se resuelve con la lista de hosts.
   */
  public getAll(
    disabled: number | null = null,
    offset: number | null = null,
    limit: number | null = null
  ): Promise<any> {
    const queryParams = { disabled, offset, limit };
    const params = Object.entries(queryParams).reduce(
      (acc, [key, value]) =>
        value !== null ? acc.set(key, value.toString()) : acc,
      new HttpParams()
    );

    return firstValueFrom(
      this.http.get(environment.apiUrl + this.url, { params })
    );
  }

  /**
   * Método para crear un nuevo registro de host en la API.
   * Realiza una solicitud HTTP POST para enviar los datos del host al servidor.
   *
   * @param host - Objeto que contiene la información del host a ser creado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la creación del host.
   */
  create(host: IHost): Promise<any> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + this.url, {
        name: host.name,
        type: host.type,
        disabled: host.disabled,
      })
    );
  }

  /**
   * Método para actualizar un host en la API.
   * Realiza una solicitud HTTP PUT para enviar los datos actualizados del host al servidor.
   *
   * @param host - Objeto que contiene la información del host con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la actualización del host.
   */
  update(host: IHost): Promise<any> {
    return firstValueFrom(
      this.http.put(`${environment.apiUrl + this.url}/${host.id}`, {
        name: host.name,
        type: host.type,
        disabled: host.disabled,
      })
    );
  }

  /**
   * Método para eliminar un host en la API.
   * Realiza una solicitud HTTP DELETE para eliminar el host basado en su ID.
   *
   * @param host - Objeto que contiene la información del host con los cambios a ser eliminado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la eliminación del host.
   */
  delete(host: IHost): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${environment.apiUrl + this.url}/${host.id}`)
    );
  }
}
